class KronoCodForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[data-cod-submit]');
    this.message = this.querySelector('[data-cod-message]');
    this.whatsappLink = this.querySelector('[data-cod-whatsapp]');
    this.inFlight = false;
    this.form?.addEventListener('submit', (event) => this.submit(event));
  }

  normalizePhone(value) {
    const digits = value.replace(/\D/g, '');
    if (/^0[5-7]\d{8}$/.test(digits)) return `+212${digits.slice(1)}`;
    if (/^212[5-7]\d{8}$/.test(digits)) return `+${digits}`;
    return '';
  }

  packState() {
    const builder = document.querySelector('krono-pack-builder');
    const size = builder?.querySelector('input[name="krono-pack-size"]:checked');
    const colors = [...(builder?.querySelectorAll('[data-color-group]') || [])].map(
      (group) => group.querySelector('[data-color-code]:checked')?.value || ''
    );
    return {
      variantId: size?.dataset.variantId || '',
      size: size?.value || '',
      colors,
      composition: builder?.querySelector('[data-pack-preset-property]')?.value || '',
      quantity: Number(builder?.querySelector('input[name="quantity"]')?.value || 1),
    };
  }

  setMessage(text, state) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.dataset.state = state;
  }

  fingerprint(payload) {
    return [payload.phone, payload.variantId, payload.quantity, ...payload.colors].join('|');
  }

  async submit(event) {
    event.preventDefault();
    if (this.inFlight || !this.form) return;

    const phoneInput = this.form.elements.phone;
    const phone = this.normalizePhone(phoneInput.value);
    phoneInput.setCustomValidity(phone ? '' : this.dataset.phoneError);
    if (!this.form.reportValidity()) return;

    const pack = this.packState();
    if (!pack.variantId || pack.colors.some((color) => !color)) {
      this.setMessage(this.dataset.packError, 'error');
      document.querySelector('krono-pack-builder')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const data = new FormData(this.form);
    const payload = {
      fullName: String(data.get('full_name') || '').trim(),
      phone,
      city: String(data.get('city') || '').trim(),
      address: String(data.get('address') || '').trim(),
      note: String(data.get('note') || '').trim(),
      productId: this.dataset.productId,
      productTitle: this.dataset.productTitle,
      currency: this.dataset.currency,
      price: this.dataset.price,
      locale: document.documentElement.lang,
      ...pack,
    };
    const fingerprint = this.fingerprint(payload);
    let previous = null;
    try {
      previous = JSON.parse(localStorage.getItem('krono-cod-last') || 'null');
    } catch {
      localStorage.removeItem('krono-cod-last');
    }
    if (previous?.fingerprint === fingerprint && Date.now() - previous.time < 120000) {
      this.setMessage(this.dataset.duplicateError, 'error');
      return;
    }
    if (!this.dataset.endpoint) {
      this.setMessage(this.dataset.endpointError, 'error');
      return;
    }

    this.inFlight = true;
    this.submitButton.disabled = true;
    this.submitButton.setAttribute('aria-busy', 'true');
    this.setMessage(this.dataset.loading, 'loading');
    const idempotencyKey = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    try {
      const response = await fetch(this.dataset.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify({ ...payload, idempotencyKey }),
      });
      if (!response.ok) throw new Error(`COD endpoint returned ${response.status}`);
      localStorage.setItem('krono-cod-last', JSON.stringify({ fingerprint, time: Date.now() }));
      this.setMessage(this.dataset.success, 'success');
      this.form.reset();
      if (this.whatsappLink) {
        const text = [
          this.dataset.whatsappIntro,
          payload.productTitle,
          `${this.dataset.sizeLabel}: ${payload.size}`,
          `${this.dataset.compositionLabel}: ${payload.composition}`,
          `${this.dataset.quantityLabel}: ${payload.quantity}`,
          `${this.dataset.cityLabel}: ${payload.city}`,
        ].join('\n');
        this.whatsappLink.href = `${this.whatsappLink.dataset.base}?text=${encodeURIComponent(text)}`;
        this.whatsappLink.hidden = false;
      }
    } catch (error) {
      console.error('[Krono COD]', error);
      this.setMessage(this.dataset.networkError, 'error');
    } finally {
      this.inFlight = false;
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('krono-cod-form')) customElements.define('krono-cod-form', KronoCodForm);
